// Lead verification on a page: the badge, what they use now and where we found them,
// and the "How we checked this" list. Quotes are strangers' words, so they're text.

import type { CheckResult, VerificationFacts, VerifyCheck, VerifyDecision } from '@/api/types'
import { Badge, InlineAlert } from '@/components/ui'
import { formatDate, formatDateTime } from '@/lib/format'
import { CHECK_RESULT_LABELS, CHECK_RESULT_TONE, CONTACT_LABELS, CONTACT_TONE, PLATFORM_STATUS_NOTE, REGISTRATION_LABELS, REGISTRATION_TONE, VERIFY_LABELS, VERIFY_TONE } from '@/lib/growth'
import { PlatformName } from './platformShared'
import { SafeExternalLink } from './shared'

type VerifyView = Pick<VerificationFacts, 'verify_status'>

export function VerifyBadge({ facts }: { facts: VerifyView }) {
  return <Badge tone={VERIFY_TONE[facts.verify_status]}>{VERIFY_LABELS[facts.verify_status]}</Badge>
}

/** The positive signals, shown only when they're there: having neither holds nothing back. */
export function SeasonSignals({ facts }: { facts: Pick<VerificationFacts, 'current_schedule' | 'registration_status'> }) {
  const open = facts.registration_status === 'open' || facts.registration_status === 'upcoming'
  if (!facts.current_schedule && !open) return null
  return (
    <span className="flex flex-wrap gap-1">
      {open ? <Badge tone={REGISTRATION_TONE[facts.registration_status]}>Registration {REGISTRATION_LABELS[facts.registration_status].toLowerCase()}</Badge> : null}
      {facts.current_schedule ? <Badge tone="good">Current schedule</Badge> : null}
    </span>
  )
}

/** The badge and its signals, for a table cell. */
export function VerifyCell({ facts }: { facts: VerifyView & Pick<VerificationFacts, 'current_schedule' | 'registration_status'> }) {
  return (
    <span className="grid justify-items-start gap-1">
      <VerifyBadge facts={facts} />
      <SeasonSignals facts={facts} />
    </span>
  )
}

/** What they use now, and where we found them underneath: the two are different questions. */
export function CurrentPlatform({ facts, linked, text }: { facts: Pick<VerificationFacts, 'platform_status' | 'found_on_platform' | 'platform_abandoned'>; linked: { id: number; name: string } | null; text: string }) {
  const note = PLATFORM_STATUS_NOTE[facts.platform_status]
  const found = facts.found_on_platform && facts.found_on_platform !== linked?.name ? facts.found_on_platform : null
  return (
    <span className="grid gap-0.5">
      <PlatformName linked={linked} text={text} />
      {note ? <span className="text-xs text-warn">{note}</span> : null}
      {found ? <span className="text-xs text-muted">found on {found}{facts.platform_abandoned ? ', left it' : ''}</span> : null}
    </span>
  )
}

const CHECK_MARK: Record<CheckResult, string> = { pass: 'Yes', info: 'Note', warn: 'Look', fail: 'No' }

function CheckRow({ check }: { check: VerifyCheck }) {
  return (
    <li className="grid gap-1 border-b border-line pb-2.5 last:border-b-0 last:pb-0">
      <p className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-ink">
        <Badge tone={CHECK_RESULT_TONE[check.result]}>
          <span className="sr-only">{CHECK_RESULT_LABELS[check.result]}: </span>
          {CHECK_MARK[check.result]}
        </Badge>
        {check.label}
      </p>
      <p className="break-words text-[13px] text-ink">{check.summary}</p>
      {check.quote && check.quote !== check.summary ? <blockquote className="break-words border-l-2 border-line-strong pl-3 text-[13px] text-muted">{check.quote}</blockquote> : null}
      {check.url ? <SafeExternalLink href={check.url} /> : null}
    </li>
  )
}

/** Every check, its result, and the evidence with links. */
export function VerifyDetails({ facts }: { facts: Pick<VerificationFacts, 'verify_status' | 'verify_checks' | 'verify_reasons' | 'verified_at' | 'contact_status'> }) {
  if (!facts.verified_at) {
    return (
      <div className="grid gap-2">
        {facts.verify_reasons.map((r) => (
          <p key={r.text} className="break-words text-[13px] text-ink">{r.text}</p>
        ))}
        {/* A lead marked by hand before any check: the call stands, nothing has been read. */}
        <p className="text-[13px] text-muted">{facts.verify_status === 'unverified' ? 'Not verified yet.' : 'No check has read this lead yet.'}</p>
      </div>
    )
  }
  return (
    <div className="grid gap-3">
      <p className="flex flex-wrap items-center gap-2 text-[13px] text-muted">
        <VerifyBadge facts={facts} />
        <Badge tone={CONTACT_TONE[facts.contact_status]}>{CONTACT_LABELS[facts.contact_status]}</Badge>
        <span>Checked {formatDateTime(facts.verified_at)}</span>
      </p>
      <ul aria-label="How we checked this" className="grid gap-2.5">
        {facts.verify_checks.map((c) => (
          <CheckRow key={c.key} check={c} />
        ))}
      </ul>
    </div>
  )
}

/**
 * A person's call, while it holds: who, when, and their note. A run over many leads
 * leaves it; Verify again checks afresh and replaces it.
 */
export function DecisionNote({ decision }: { decision: VerifyDecision }) {
  const who = decision.by ? decision.by.name || decision.by.email : 'someone'
  return (
    <p className="break-words text-[13px] text-ink">
      Marked {decision.status === 'verified' ? 'verified' : 'failed'} by {who} on {formatDate(decision.at)}
      {decision.note ? `: ${decision.note}` : '.'}{' '}
      <span className="text-muted">Verify again replaces this with a fresh check.</span>
    </p>
  )
}

/** Why a failed candidate stays out, in the check's own words. */
export function FailedReasons({ facts }: { facts: Pick<VerificationFacts, 'verify_status' | 'verify_reasons'> }) {
  if (facts.verify_status !== 'failed') return null
  return (
    <InlineAlert tone="bad" title="Failed verification">
      <ul className="grid gap-1">
        {facts.verify_reasons.map((r) => (
          <li key={r.text} className="break-words">
            {r.text} {r.url ? <SafeExternalLink href={r.url} /> : null}
          </li>
        ))}
      </ul>
    </InlineAlert>
  )
}
